import axios from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getCountryListRequest,
	getCountryListSuccess,
	getCountryPagedListRequest,
	getCountryPagedListSuccess,
	getCountryPagedListFailure,
	getCountryDetailsRequest,
	getCountryDetailsSuccess,
	getCountryDetailsFailure,
	updateCountryRequest,
	saveCountrySuccess,
	saveCountryFailure,
} from './reducer';
import { getCountryLastListParams } from './selectors';
import {
	TCountryDetails,
	TCountryIdRequest,
	TCountryListResponsePayload,
	TCountryManagementRequest,
	TCountryPagedListResponse,
	TUpdateCountryRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TCountryManagementRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* getCountryListRequestWorker() {
	try {
		const response: TCountryListResponsePayload = yield call(api.country.getCountryList);
		yield put(getCountryListSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(countryInitState());
	}
}

// ========================================== Country management (reference table)

function* getCountryPagedListWorker({ payload }: PayloadAction<TCountryManagementRequest>) {
	try {
		const response: TCountryPagedListResponse = yield call(
			api.country.getCountryPagedList,
			payload,
		);
		yield put(getCountryPagedListSuccess(response));
	} catch (error) {
		yield put(getCountryPagedListFailure());
		notifyError(error, 'Failed to load countries');
	}
}

function* refetchCountryPagedList() {
	const lastParams: TCountryManagementRequest | null = yield select(getCountryLastListParams);
	const response: TCountryPagedListResponse = yield call(
		api.country.getCountryPagedList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getCountryPagedListSuccess(response));
}

function* getCountryDetailsWorker({ payload }: PayloadAction<TCountryIdRequest>) {
	try {
		const response: TCountryDetails = yield call(api.country.getCountryDetails, payload);
		yield put(getCountryDetailsSuccess(response));
	} catch (error) {
		yield put(getCountryDetailsFailure());
		notifyError(error, 'Failed to load country');
	}
}

function* updateCountryWorker({ payload }: PayloadAction<TUpdateCountryRequest>) {
	try {
		const response: TCountryDetails = yield call(api.country.updateCountry, payload);
		yield put(getCountryDetailsSuccess(response));
		yield call(refetchCountryPagedList);
		yield put(saveCountrySuccess());
		notificationContainer('Country was successfully updated', 'success');
	} catch (error) {
		yield put(saveCountryFailure());
		notifyError(error, 'Failed to update country');
	}
}

export function* countrySaga() {
	yield takeEvery(getCountryListRequest.type, getCountryListRequestWorker);
	yield takeLatest(getCountryPagedListRequest.type, getCountryPagedListWorker);
	yield takeLatest(getCountryDetailsRequest.type, getCountryDetailsWorker);
	yield takeLatest(updateCountryRequest.type, updateCountryWorker);
}
